import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Accordion, Badge, Button, MultiSelect, Skeleton, SwatchButton, TextArea, Toggle, type MultiSelectOption } from '@/shared/ui'
import { useEffectivePermissions } from '@/auth/useEffectivePermissions'
import {
  useBotModels,
  useBotSettings,
  useModelCatalog,
  useMonthUsage,
  useOrgSettings,
  usePatchOrgSettings,
  usePatchSettings,
  useSyncModels,
  useToggleModel,
} from './hooks/useBot'
import { formatEur } from './botUtils'
import { BOT_SWATCHES, DEFAULT_PROVIDER_SWATCH } from './botColors'
import { getModelTagline, estimateAnswerEur, formatEstimate } from './botModelMeta'
import type { BotOrgSettingEntry } from './api/botApi'

type BotProvider = 'anthropic' | 'openai' | 'google'

// Row order for the color picker — same three providers as ReportTab's
// breakdown, so `report.providers.*` doubles as the display name here too.
const PROVIDERS: BotProvider[] = ['anthropic', 'openai', 'google']

const ORG_SETTING_MAX_LEN = 4000

/**
 * One "Richtlinien"/"Wissensbasis" field of the org-settings section below:
 * label + char counter, a helper hint line, the textarea itself, and a
 * "Zuletzt geändert von …" line once the key has a saved row. Pulled out of
 * SettingsTab's body since it's rendered twice with identical structure —
 * not exported, no hooks of its own (Rules of Hooks is a non-issue here).
 */
function OrgSettingField({
  id,
  label,
  hint,
  value,
  onChange,
  disabled,
  entry,
  language,
  t,
}: {
  id: string
  label: string
  hint: string
  value: string
  onChange: (value: string) => void
  disabled: boolean
  entry: BotOrgSettingEntry | null | undefined
  language: string
  t: (key: string, options?: Record<string, unknown>) => string
}) {
  const overLimit = value.length > ORG_SETTING_MAX_LEN
  const hintId = `${id}-hint`
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <label htmlFor={id} className="block text-xs font-medium text-on-surface">{label}</label>
        <span className={`text-[10px] tabular-nums ${overLimit ? 'text-error' : 'text-on-surface-variant'}`}>
          {t('composer.counter', { count: value.length, max: ORG_SETTING_MAX_LEN })}
        </span>
      </div>
      <p id={hintId} className="text-xs text-on-surface-variant">{hint}</p>
      <TextArea
        id={id}
        aria-describedby={hintId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={4}
        maxLength={ORG_SETTING_MAX_LEN}
        disabled={disabled}
        error={overLimit ? t('settings.org.tooLong', { max: ORG_SETTING_MAX_LEN }) : undefined}
      />
      {entry && (
        <p className="text-[11px] text-on-surface-variant">
          {t('settings.org.lastChanged', {
            name: entry.updatedBy ?? '—',
            date: entry.updatedAt ? new Date(entry.updatedAt).toLocaleDateString(language) : '—',
          })}
        </p>
      )}
    </div>
  )
}

/**
 * "Einstellungen" tab body (Design Spec §9/§13 O5-O6). Flat data surface —
 * lives inside BotPanel/BotCompareDock's glass chrome. Every control saves
 * on change (PATCH /api/bot/settings) — no submit button; a "Gespeichert"
 * Badge flashes for ~1.6s after each successful save.
 */
export function SettingsTab() {
  const { t, i18n } = useTranslation('bot')
  const { data: models } = useBotModels()
  const { data: settings } = useBotSettings()
  const { data: monthUsage, isError: usageError } = useMonthUsage()
  const patchSettings = usePatchSettings()
  // Modellkatalog (last section, IT.Admin only — see the gated block near
  // the bottom of the JSX below). `enabled: isAdmin` — not a JSX-level
  // condition — is what actually stops the request from firing for a
  // non-admin: SettingsTab itself is unconditionally mounted whenever the
  // Settings tab is active, so the hook call must stay unconditional too
  // (Rules of Hooks) and lean on react-query's own `enabled` gate instead,
  // exactly like useBotSettings/useMonthUsage's `opts.enabled` above.
  const { isAdmin } = useEffectivePermissions()
  const catalog = useModelCatalog({ enabled: isAdmin })
  const syncModels = useSyncModels()
  const toggleModel = useToggleModel()
  // Org guardrails/knowledge (migration 268) — same `enabled: isAdmin` gate
  // as the catalog above, so GET /bot/org-settings never fires for a
  // non-admin either.
  const orgSettings = useOrgSettings({ enabled: isAdmin })
  const patchOrgSettings = usePatchOrgSettings()

  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  // Timer id lives in a ref (not state gated on `saved`) so a second save
  // landing while the badge is still showing restarts the 1.6s window
  // instead of no-opping on a true->true update and letting the *first*
  // save's timer close the badge early.
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
    }
  }, [])

  function onSaveSuccess() {
    setError(null)
    setSaved(true)
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
    savedTimerRef.current = setTimeout(() => setSaved(false), 1600)
  }
  function onSaveError() {
    setError(t('errors.generic'))
  }

  // Org guardrails/knowledge (migration 268) — unlike the auto-save fields
  // above, these two textareas save via an explicit Button (owner ask: two
  // free-text fields an admin edits deliberately, not a per-keystroke
  // PATCH). Local text state is seeded ONCE from the GET response
  // (`orgSeeded` guard, same one-shot pattern as useSeedViewMode in
  // hooks/useBot.ts) and from then on IS the source of truth; `savedOrg*`
  // tracks the last-persisted value directly from the mutation itself
  // (not from the query refetch it triggers) so "unchanged" flips back to
  // true the instant a save succeeds, without a refetch-timing race.
  const [orgSeeded, setOrgSeeded] = useState(false)
  const [guardrailsText, setGuardrailsText] = useState('')
  const [knowledgeText, setKnowledgeText] = useState('')
  const [savedGuardrailsText, setSavedGuardrailsText] = useState('')
  const [savedKnowledgeText, setSavedKnowledgeText] = useState('')

  useEffect(() => {
    if (!orgSettings.data || orgSeeded) return
    const g = orgSettings.data.guardrails?.text ?? ''
    const k = orgSettings.data.knowledge?.text ?? ''
    setGuardrailsText(g)
    setKnowledgeText(k)
    setSavedGuardrailsText(g)
    setSavedKnowledgeText(k)
    setOrgSeeded(true)
  }, [orgSettings.data, orgSeeded])

  const orgNotMigrated = orgSettings.data?.persisted === false
  const orgDirty = guardrailsText !== savedGuardrailsText || knowledgeText !== savedKnowledgeText
  const orgOverLimit = guardrailsText.length > 4000 || knowledgeText.length > 4000
  const orgSaveDisabled = !orgSeeded || patchOrgSettings.isPending || !orgDirty || orgOverLimit || orgNotMigrated

  function handleOrgSave() {
    patchOrgSettings.mutate(
      { guardrails: guardrailsText, knowledge: knowledgeText },
      {
        onSuccess: () => {
          onSaveSuccess()
          setSavedGuardrailsText(guardrailsText)
          setSavedKnowledgeText(knowledgeText)
        },
        onError: onSaveError,
      },
    )
  }

  // Unconfigured models are listed (with a suffix), not hidden — mirrors
  // BotComposer.tsx's identical list — so a user can still see and pin a
  // not-yet-configured model as a future default (Design Spec §1/§10).
  // note = tagline + estimated cost per answer (Design Spec round 2 §4).
  // Unconfigured rows keep the notConfigured label suffix (unchanged) but get
  // the tagline ONLY — no cost estimate for a model the user can't actually
  // use yet, even though bot.ModelPrice has pricing data for it already.
  const modelOptions: MultiSelectOption[] = (models ?? []).map((m) => {
    const tagline = getModelTagline(t, m.modelId)
    const estimate = m.configured ? estimateAnswerEur(m.price) : null
    const note = estimate != null ? `${tagline} · ${formatEstimate(estimate, t, i18n.language)}` : tagline
    return {
      value: m.modelId,
      label: m.configured ? `${m.label}` : `${m.label} ${t('composer.notConfigured')}`,
      note,
    }
  })

  function handleDefaultModelsChange(values: string[]) {
    patchSettings.mutate({ defaultModels: values.slice(0, 3) }, { onSuccess: onSaveSuccess, onError: onSaveError })
  }

  function handleMemoryToggle(checked: boolean) {
    patchSettings.mutate({ memoryEnabled: checked }, { onSuccess: onSaveSuccess, onError: onSaveError })
  }

  function handleColorChange(provider: BotProvider, swatchId: string) {
    patchSettings.mutate(
      { providerColors: { ...(settings?.providerColors ?? {}), [provider]: swatchId } },
      { onSuccess: onSaveSuccess, onError: onSaveError },
    )
  }

  const orgEur = (monthUsage?.monthSpendMicroEur ?? 0) / 1_000_000
  const capEur = monthUsage?.capEur ?? 0
  const mineEur = (monthUsage?.mine ?? 0) / 1_000_000

  return (
    <div className="space-y-5">
      <div className="flex justify-end min-h-5">{saved && <Badge tone="success">{t('common.saved')}</Badge>}</div>

      {error && <p role="alert" className="text-xs text-error">{error}</p>}

      <div className="space-y-1.5">
        <span className="block text-xs font-medium text-on-surface">{t('settings.defaultModels')}</span>
        <MultiSelect
          ariaLabel={t('settings.defaultModels')}
          options={modelOptions}
          selected={settings?.defaultModels ?? []}
          onChange={handleDefaultModelsChange}
          max={3}
          placeholder={t('composer.maxModels', { max: 3 })}
          searchPlaceholder={t('composer.search')}
          noResultsLabel={t('composer.noResults')}
          removeLabel={(optLabel) => t('composer.removeModel', { label: optLabel })}
          size="compact"
          disabled={!settings || patchSettings.isPending}
        />
      </div>

      <div className="space-y-1">
        <Toggle
          checked={settings?.memoryEnabled ?? true}
          onChange={handleMemoryToggle}
          disabled={!settings || patchSettings.isPending}
          label={t('memory.toggleLabel')}
        />
        <p className="text-xs text-on-surface-variant">{t('memory.explain')}</p>
      </div>

      <div className="space-y-2">
        <span className="block text-xs font-medium text-on-surface">{t('settings.colors')}</span>
        {PROVIDERS.map((provider) => {
          // Effective color = server value merged over the DEFAULT_PROVIDER_SWATCH
          // fallback — the defaults count as "taken" too, otherwise two
          // untouched providers could collide out of the box (owner feedback
          // round 2 §1). Computed for ALL providers (not just this row) so the
          // exclusivity check below can see every other provider's own value.
          const effectiveColor = (p: BotProvider) => settings?.providerColors?.[p] ?? DEFAULT_PROVIDER_SWATCH[p]
          const selected = effectiveColor(provider)
          return (
            <div key={provider} className="flex items-center gap-3">
              <span className="w-28 shrink-0 text-xs text-on-surface-variant truncate">{t(`report.providers.${provider}`)}</span>
              <div className="flex items-center gap-1.5 flex-wrap">
                {BOT_SWATCHES.map((sw) => {
                  const isSelected = sw.id === selected
                  // Taken by ANOTHER provider's effective color -> render
                  // disabled, not clickable. A provider's own current
                  // selection is never disabled by this check (isSelected
                  // short-circuits it) — including the legacy-collision case
                  // where two providers already share the same swatch: both
                  // rows still show it selected and stay clickable, since
                  // each provider's own row exempts its own color.
                  const takenByOther = !isSelected && PROVIDERS.some((p) => p !== provider && effectiveColor(p) === sw.id)
                  return (
                    <SwatchButton
                      key={sw.id}
                      color={sw.dot}
                      selected={isSelected}
                      dimmed={takenByOther}
                      aria-disabled={takenByOther ? true : undefined}
                      aria-label={`${t(`report.providers.${provider}`)} — ${sw.id}`}
                      title={takenByOther ? t('settings.colorTaken') : undefined}
                      disabled={!settings || patchSettings.isPending || takenByOther}
                      onClick={() => handleColorChange(provider, sw.id)}
                    />
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>

      {/* Hidden while GET /api/bot/usage/month has failed — an unpriced
          "0,00 € / 0 €" line would misreport spend as zero rather than
          unknown. */}
      {!usageError && (
        <p className="text-xs text-on-surface-variant tabular-nums">
          {t('settings.usage', { org: formatEur(orgEur, 2), cap: formatEur(capEur, 0), mine: formatEur(mineEur, 2) })}
        </p>
      )}

      {/* Bot-Richtlinien & Wissen (org guardrails/knowledge, migration 268)
          — IT.Admin only, same invisible-not-just-disabled non-admin
          treatment as Modellkatalog below (see its own comment). Placed
          directly before Modellkatalog per the owner's mental model: "set
          the rules, then manage the models". */}
      {isAdmin && (
        <div className="space-y-3 pt-4 border-t border-outline-variant">
          <div className="space-y-0.5">
            <span className="block text-xs font-medium text-on-surface">{t('settings.org.title')}</span>
            <p className="text-xs text-on-surface-variant">{t('settings.org.subtitle')}</p>
          </div>

          {orgSettings.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : orgSettings.isError ? (
            <p role="alert" className="text-xs text-error">{t('errors.generic')}</p>
          ) : (
            <>
              <OrgSettingField
                id="bot-org-guardrails"
                label={t('settings.org.guardrails')}
                hint={t('settings.org.guardrailsHint')}
                value={guardrailsText}
                onChange={setGuardrailsText}
                disabled={orgNotMigrated || patchOrgSettings.isPending}
                entry={orgSettings.data?.guardrails}
                language={i18n.language}
                t={t}
              />
              <OrgSettingField
                id="bot-org-knowledge"
                label={t('settings.org.knowledge')}
                hint={t('settings.org.knowledgeHint')}
                value={knowledgeText}
                onChange={setKnowledgeText}
                disabled={orgNotMigrated || patchOrgSettings.isPending}
                entry={orgSettings.data?.knowledge}
                language={i18n.language}
                t={t}
              />

              {orgNotMigrated && <p className="text-xs text-on-surface-variant">{t('settings.org.notMigrated')}</p>}

              <Button variant="secondary" size="sm" loading={patchOrgSettings.isPending} disabled={orgSaveDisabled} onClick={handleOrgSave}>
                {t('settings.org.save')}
              </Button>
            </>
          )}
        </div>
      )}

      {/* Modellkatalog — IT.Admin only. A non-admin renders nothing here at
          all (not even a collapsed/disabled shell) — see the `enabled:
          isAdmin` gate on `catalog` above, which keeps GET
          /bot/models/catalog from ever firing for them either. */}
      {isAdmin && (
        <div className="space-y-2 pt-4 border-t border-outline-variant">
          <div className="space-y-0.5">
            <span className="block text-xs font-medium text-on-surface">{t('settings.catalog.title')}</span>
            <p className="text-xs text-on-surface-variant">{t('settings.catalog.subtitle')}</p>
          </div>

          <Button
            variant="secondary"
            size="sm"
            icon="sync"
            loading={syncModels.isPending}
            disabled={syncModels.isPending}
            onClick={() => syncModels.mutate()}
          >
            {t('settings.catalog.sync')}
          </Button>

          {syncModels.data && (
            <div className="space-y-0.5">
              <p className="text-xs text-on-surface-variant">
                {syncModels.data.added.length > 0
                  ? t('settings.catalog.added', { count: syncModels.data.added.length })
                  : t('settings.catalog.none')}
              </p>
              {Object.keys(syncModels.data.errors).map((provider) => (
                <p key={provider} className="text-xs text-on-surface-variant">
                  {t('settings.catalog.providerError', { provider: t(`report.providers.${provider}`) })}
                </p>
              ))}
            </div>
          )}

          <div className="space-y-1 pt-1">
            {(() => {
              // Cross-reference the last sync's newly-added rows against the
              // catalog list below so an admin can spot which currently-disabled
              // toggles are new (vs. pre-existing) without diffing by memory
              // against the summary count above.
              const newlyAdded = new Set(
                (syncModels.data?.added ?? []).map((a) => `${a.provider}:${a.modelId}`),
              )
              return catalog.isLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              ) : catalog.isError ? (
                <p role="alert" className="text-xs text-error">{t('errors.generic')}</p>
              ) : (
                /* One Accordion panel per provider (owner 2026-09-01: 64
                   OpenAI models made the flat list unusable). Collapsed by
                   default, several may be open (multiple). Header shows
                   enabled/total at a glance. Toggles are NOT globally
                   disabled anymore — useToggleModel flips optimistically. */
                <Accordion
                  multiple
                  items={PROVIDERS.flatMap((provider) => {
                    const rows = (catalog.data ?? []).filter((m) => m.provider === provider)
                    if (!rows.length) return []
                    const enabledCount = rows.filter((m) => m.isEnabled).length
                    return [{
                      id: provider,
                      header: (
                        <span className="block text-[10px] font-medium uppercase tracking-wider text-on-surface-variant">
                          {t(`report.providers.${provider}`)} · {enabledCount} / {rows.length}
                        </span>
                      ),
                      content: (
                        <div>
                          {rows.map((m) => {
                            const isNew = newlyAdded.has(`${m.provider}:${m.modelId}`)
                            return (
                              <div key={m.id} className={`flex items-center gap-2 py-1.5 ${m.configured ? '' : 'opacity-60'}`}>
                                <Toggle
                                  checked={m.isEnabled}
                                  onChange={(checked) => toggleModel.mutate({ id: m.id, isEnabled: checked })}
                                  disabled={toggleModel.isPending && toggleModel.variables?.id === m.id}
                                  size="sm"
                                  ariaLabel={m.label}
                                />
                                <span className="min-w-0 flex-1 text-sm text-on-surface truncate">{m.label}</span>
                                <span className="hidden sm:inline min-w-0 max-w-[40%] text-xs text-on-surface-variant truncate">{m.modelId}</span>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  {isNew && <Badge tone="info" size="sm">{t('settings.catalog.new')}</Badge>}
                                  {!m.configured && <Badge tone="neutral" size="sm">{t('settings.catalog.notConfigured')}</Badge>}
                                  {m.price == null && <Badge tone="neutral" size="sm">{t('settings.catalog.noPrice')}</Badge>}
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      ),
                    }]
                  })}
                />
              )
            })()}
          </div>
        </div>
      )}
    </div>
  )
}

export default SettingsTab
